import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import SelectController from "@/common/components/SelectController";
import { useEquipmentCategories } from "@/features/equipments/hooks/useEquipmentCategories";
import { useEquipmentManufacturers } from "@/common/hooks/queries/useEquipmentManufacturers";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import { CreateScrapRuleSchema } from "../schemas/createScrapRule.schema";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";
import { useEquipmentModels } from "@/features/equipments/hooks/useEquipmentModels";
import { CompanyEquipmentCategoriesTransformer } from "@/common/services/companyEquipmentCategoriesTransformer";
import { EquipmentManufacturersTransformer } from "@/common/services/equipmentManufacturersTransformer";
import { CompanyBranchesTransformer } from "@/features/companyBranches/services/companyBranchesTransformer";
import { EquipmentModelsTransformer } from "@/common/services/equipmentModelsTransformer";

interface AccordionScrapRulesEquipmentsProps {
  register: UseFormRegister<CreateScrapRuleSchema>
  errors: FieldErrors<CreateScrapRuleSchema>
  control: Control<CreateScrapRuleSchema>
  disabled?: boolean
}

export default function AccordionScrapRulesEquipments({errors, control, disabled}: AccordionScrapRulesEquipmentsProps) {
  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')
  const [searchCompanyBranch, setSearchCompanyBranch] = useDebounce('')
  const [searchCompanyEquipments, setSearchCompanyEquipments] = useDebounce('')
  const [searchEquipmentManufacturers, setSearchEquipmentManufacturers] = useDebounce('')
  const [searchEquipmentModels, setSearchEquipmentModels] = useDebounce('')

  const { data: equipmentCategoryOptions, isPending: equipmentCategoryOptionsIsPending } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory }, 
    mapper: Mapper.mapWithTransformer({ transformer: CompanyEquipmentCategoriesTransformer.optionTransformer }),
  })

  const { data: companyBranchesOptions, isPending: companyBranchesOptionsIsPending } = useAvailableCompanyBranches({
    filters: { search: searchCompanyBranch },
    mapper: Mapper.mapWithTransformer({ transformer: CompanyBranchesTransformer.optionsTransformer() }),
  })

  const { data: equipmentsOptions, isPending: equipmentsOptionsIsPending } = useEquipments({
    filters: { search: searchCompanyEquipments },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentsTransformer.optionsTransformer() }),
  })
  
  const { data: equipmentManufacturersOptions, isPending: equipmentManufacturersOptionsIsPending } = useEquipmentManufacturers({
    filters: { search: searchEquipmentManufacturers },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentManufacturersTransformer.optionsTransformer() }),
  })
  
  const { data: equipmentModelsOptions, isPending: equipmentModelsOptionsIsPending } = useEquipmentModels({
    filters: { search: searchEquipmentModels },
    mapper: Mapper.mapWithTransformer({ transformer: EquipmentModelsTransformer.optionsTransformer() }),
  })

  return (
    <Accordion
      name="scrapRulesEquipments"
      head={<p className="text-base">Equipamentos</p>}
      body={
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="equipment_category_ids"
              label="Categorias de equipamento"
              onInputChange={setSearchEquipmentCategory}
              options={equipmentCategoryOptions || []}
              isMulti
              errorMessage={errors?.equipment_category_ids?.message as string}
              isDisabled={equipmentCategoryOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <SelectController
              control={control}
              name="branch_ids"
              label="Filiais"
              onInputChange={setSearchCompanyBranch}
              options={companyBranchesOptions || []}
              isMulti
              errorMessage={errors?.branch_ids?.message as string}
              isDisabled={companyBranchesOptionsIsPending || disabled}
              optionLabelFormat="icon"
            />
            <Box className="flex flex-col gap-4 bg-white-100 p-4">
              <SelectController
                control={control}
                name="manufacturer_ids"
                label="Fabricantes"
                onInputChange={setSearchEquipmentManufacturers}
                options={equipmentManufacturersOptions || []}
                isMulti
                errorMessage={errors?.manufacturer_ids?.message as string}
                isDisabled={equipmentManufacturersOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_model_ids"
                label="Modelos"
                onInputChange={setSearchEquipmentModels}
                options={equipmentModelsOptions || []}
                isMulti
                errorMessage={errors?.equipment_model_ids?.message as string}
                isDisabled={equipmentModelsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
              <SelectController
                control={control}
                name="equipment_ids"
                label="Equipamentos na frota"
                onInputChange={setSearchCompanyEquipments}
                options={equipmentsOptions || []}
                isMulti
                errorMessage={errors?.equipment_ids?.message as string}
                isDisabled={equipmentsOptionsIsPending || disabled}
                optionLabelFormat="icon"
              />
            </Box>
          </div>
        </div>
      }
    />
  )
}
